import { useState } from 'react';
import type {
  EmittersData,
  EmitterType,
  PerformanceSignal,
  LowTempDesign,
  PrimarySizeBand,
  LayoutType,
  HydraulicSeparation,
  SludgeEvidence,
} from '../types';

const EMITTER_OPTIONS: { value: EmitterType; label: string }[] = [
  { value: 'radiators', label: 'Radiators' },
  { value: 'ufh', label: 'Underfloor heating' },
  { value: 'mixed', label: 'Mixed (rads + UFH)' },
  { value: 'unknown', label: 'Unknown' },
];

const PERF_OPTIONS: { value: PerformanceSignal; label: string }[] = [
  { value: 'all-heat-well', label: 'Rooms heat well' },
  { value: 'some-struggle', label: 'Some rooms struggle' },
  { value: 'many-struggle', label: 'Many rooms struggle' },
  { value: 'unknown', label: 'Unknown' },
];

const LOW_TEMP_OPTIONS: { value: LowTempDesign; label: string }[] = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'unknown', label: 'Unknown' },
];

const SIZE_OPTIONS: { value: PrimarySizeBand; label: string }[] = [
  { value: '15mm', label: '15 mm' },
  { value: '22mm', label: '22 mm' },
  { value: '28mm', label: '28 mm' },
  { value: 'unknown', label: 'Unknown' },
];

const LAYOUT_OPTIONS: { value: LayoutType; label: string }[] = [
  { value: 'microbore', label: 'Microbore' },
  { value: 'one-pipe', label: 'One-pipe' },
  { value: 'two-pipe', label: 'Two-pipe' },
  { value: 'unknown', label: 'Unknown' },
];

const SEP_OPTIONS: { value: HydraulicSeparation; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'llh', label: 'Low-loss header' },
  { value: 'buffer', label: 'Buffer vessel' },
  { value: 'plate-hx', label: 'Plate heat exchanger' },
  { value: 'unknown', label: 'Unknown' },
];


const SLUDGE_OPTIONS: { value: SludgeEvidence; label: string }[] = [
  { value: 'none', label: 'No signs of sludge' },
  { value: 'possible', label: 'Possible sludge signs' },
  { value: 'likely', label: 'Likely sludge / dirty water' },
  { value: 'confirmed', label: 'Confirmed magnetite/sludge' },
  { value: 'unknown', label: 'Unknown' },
];


interface Props {
  data: EmittersData;
  onSave: (data: EmittersData) => void;
  onClose: () => void;
}

export function EmittersScreen({ data, onSave, onClose }: Props) {
  const [draft, setDraft] = useState<EmittersData>({ ...data });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);

  const set = <K extends keyof EmittersData>(key: K, value: EmittersData[K]) => {
    setDraft(prev => ({ ...prev, [key]: value }));
    setHasUnsavedChanges(true);
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      setShowDiscardDialog(true);
    } else {
      onClose();
    }
  };

  const handleSave = () => {
    setHasUnsavedChanges(false);
    onSave(draft);
  };

  return (
    <>
      <div className="hub-screen-overlay">
        <div className="hub-screen" onClick={e => e.stopPropagation()}>
          <div className="hub-screen__header">
            <h2 className="hub-screen__title">Emitters & Distribution</h2>
            <button className="hub-screen__close" onClick={handleClose}>&times;</button>
          </div>

        <div className="hub-screen__body">
          {/* Emitters */}
          <div className="hub-field">
            <label className="hub-field__label">Emitter type</label>
            <div className="hub-field__chip-group">
              {EMITTER_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  className={`hub-chip${draft.emitters === opt.value ? ' hub-chip--selected' : ''}`}
                  onClick={() => set('emitters', opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {draft.emitters === 'unknown' && (
              <div className="hub-note hub-note--warning" style={{ marginTop: '0.5rem' }}>
                Confirm emitter type before running the report. Unknown emitters reduce confidence and can block output.
              </div>
            )}
          </div>

          <div className="hub-field">
            <label className="hub-field__label">Performance signal</label>
            <div className="hub-field__chip-group">
              {PERF_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  className={`hub-chip${draft.performanceSignal === opt.value ? ' hub-chip--selected' : ''}`}
                  onClick={() => set('performanceSignal', opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>


          <div className="hub-field">
            <label className="hub-field__label">System cleanliness (for efficiency losses)</label>
            <span className="hub-field__hint">Used to score dirty-system losses and whether powerflush/filter upgrades are needed.</span>
            <div className="hub-field__chip-group">
              {SLUDGE_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  className={`hub-chip${draft.sludgeEvidence === opt.value ? ' hub-chip--selected' : ''}`}
                  onClick={() => set('sludgeEvidence', opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="hub-field">
            <label className="hub-field__label">Low-temp evidence</label>
            <span className="hub-field__hint">Only fill if you have evidence (e.g. heat loss calc, IR survey)</span>
            <div className="hub-field__chip-group">
              {LOW_TEMP_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  className={`hub-chip${draft.lowTempCapability === opt.value ? ' hub-chip--selected' : ''}`}
                  onClick={() => set('lowTempCapability', opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Distribution */}
          <div className="hub-field">
            <label className="hub-field__label">Primary pipe size band</label>
            <div className="hub-field__chip-group">
              {SIZE_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  className={`hub-chip${draft.primarySizeBand === opt.value ? ' hub-chip--selected' : ''}`}
                  onClick={() => set('primarySizeBand', opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="hub-field">
            <label className="hub-field__label">Layout type (optional)</label>
            <div className="hub-field__chip-group">
              {LAYOUT_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  className={`hub-chip${draft.layoutType === opt.value ? ' hub-chip--selected' : ''}`}
                  onClick={() => set('layoutType', opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="hub-field">
            <label className="hub-field__label">Hydraulic separation ("Is this good?" — sometimes)</label>
            <span className="hub-field__hint">
              Useful when circuits need hydraulic decoupling; unnecessary separation can add complexity without solving root causes.
            </span>
            <div className="hub-field__chip-group">
              {SEP_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  className={`hub-chip${draft.hydraulicSeparation === opt.value ? ' hub-chip--selected' : ''}`}
                  onClick={() => set('hydraulicSeparation', opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {draft.hydraulicSeparation === 'llh' && (
            <div className="hub-note">
              <strong>Low-loss header:</strong> Protects pumps from hydraulic interference but does not
              improve heat distribution performance. It is not a "buffer" and does not add thermal mass.
            </div>
          )}

          {draft.hydraulicSeparation === 'buffer' && (
            <div className="hub-note hub-note--warning">
              Buffer vessels add thermal mass, which can mask short-cycling — but they do not fix
              the root cause (oversizing or poor modulation).
            </div>
          )}
        </div>

        <div className="hub-screen__footer">
          <button className="hub-screen__button hub-screen__button--secondary" onClick={handleClose}>Cancel</button>
          <button className="hub-screen__button hub-screen__button--primary" onClick={handleSave}>
            Save
          </button>
        </div>
      </div>
    </div>

    {/* Discard changes dialog */}
    {showDiscardDialog && (
      <div className="hub-screen-overlay" style={{ zIndex: 1001 }}>
        <div className="hub-screen hub-screen--dialog" onClick={e => e.stopPropagation()}>
          <div className="hub-screen__header">
            <h2 className="hub-screen__title">Discard changes?</h2>
          </div>
          <div className="hub-screen__body">
            <p>You have unsaved changes. Are you sure you want to discard them?</p>
          </div>
          <div className="hub-screen__footer">
            <button 
              className="hub-screen__button hub-screen__button--secondary" 
              onClick={() => setShowDiscardDialog(false)}
            >
              Keep editing
            </button>
            <button 
              className="hub-screen__button hub-screen__button--danger" 
              onClick={() => {
                setShowDiscardDialog(false);
                setHasUnsavedChanges(false);
                onClose();
              }}
            >
              Discard changes
            </button>
          </div>
        </div>
      </div>
    )}
  </>
);
}
